<template>
  <v-container
    fluid
    class="grey lighten-5 fill-height align-start">
    <v-row>
      <v-col cols="12">
        <v-card
          flat
          outlined
          class="mb-4">
          <v-card-title class="headline font-weight-bold">
            <v-icon
              left
              color="primary">
              mdi-clipboard-text-clock
            </v-icon>
            Warehouse Work Logs
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              depressed
              @click="exportXLSX">
              <v-icon left>
                mdi-file-export
              </v-icon>
              Export XLSX
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <!-- Filter Section -->
          <v-card-text>
            <v-row align="center">
              <v-spacer></v-spacer>
              <v-col
                cols="12"
                md="4">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateRangeText"
                      label="Filter by Date Range"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dates"
                    range
                    @input="onDateChange"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Data Table -->
        <v-card
          flat
          outlined>
          <v-data-table
            :headers="headers"
            :items="workLogs"
            :options.sync="options"
            :items-per-page="options.itemsPerPage"
            :server-items-length="total"
            :loading="loading"
            :footer-props="{'items-per-page-options': [5, 15, 25, 50]}"
            class="w-full"
            @update:options="fetchLogs()"
          >
            <!-- Slot for Employee -->
            <template v-slot:item.employee="{ item }">
              <div class="font-weight-medium">
                {{ item.employee.firstName }} {{ item.employee.lastName }}
              </div>
              <small class="text--secondary">{{ item.employee.employeeCode }}</small>
            </template>

            <!-- Slot for Task Type -->
            <template v-slot:item.taskType="{ item }">
              <v-chip
                small
                label
                color="blue-grey lighten-5"
                class="text-caption">
                {{ item.taskType.department }}
              </v-chip>
              <span class="mt-1">
                {{ item.taskType.name }}
              </span>
            </template>

            <!-- Slot for Duration -->
            <template v-slot:item.duration="{ item }">
              {{ formatDuration(item.duration) }}
            </template>

            <!-- Slot for State -->
            <template v-slot:item.state="{ item }">
              <v-chip
                small
                :color="item.state === 'completed' ? 'green lighten-5' : 'orange lighten-5'"
                :class="item.state === 'completed' ? 'green--text' : 'orange--text'"
                class="font-weight-bold"
              >
                {{ item.state.toUpperCase() }}
              </v-chip>
            </template>

            <!-- Slot for Quantity -->
            <template v-slot:item.quantity="{ item }">
              <span class="font-weight-bold">{{ item.quantity || '-' }}</span>
            </template>

            <!-- Slot for Start/End Time -->
            <template v-slot:item.times="{ item }">
              <div class="text-caption">
                <b>Start:</b> {{ formatDateTime(item.startAt) }} <br>
                <b>End:</b> {{ formatDateTime(item.endAt) }}
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import dayjs from 'dayjs'
import WarehouseWorkTrackingProvider from '@/resources/WarehouseWorkTrackingProvider'
import * as XLSX from 'xlsx'

const provider = new WarehouseWorkTrackingProvider()

export default {
  data: () => ({
    loading: false,
    dateMenu: false,
    dates: [],
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ['createdAt'],
      sortDesc: [true]
    },
    total: 0,
    headers: [
      { text: 'Employee', value: 'employee' },
      { text: 'Department / Task', value: 'taskType' },
      { text: 'Time Period', value: 'times', sortable: false },
      { text: 'Duration', value: 'duration' },
      { text: 'Qty', value: 'quantity', align: 'center' },
      { text: 'Status', value: 'state', align: 'center' }
    ],
    workLogs: []
  }),
  computed: {
    dateRangeText () {
      return this.dates.join(' ~ ')
    }
  },
  mounted () {
    const today = new Date().toISOString().split('T')[0]
    this.dates = [today, today]
    this.fetchLogs()
  },
  methods: {
    async fetchLogs () {
      if (this.dates.length !== 2) return
      this.loading = true
      try {
        const sorted = [...this.dates].sort()
        const { data: { total, results } } = await provider.getWorkLogsByPaginate({
          startDate: sorted[0],
          endDate: sorted[1],
          page: this.options.page,
          limit: this.options.itemsPerPage,
          sortBy: this.options.sortBy[0],
          sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc'
        })
        const logsData = results || []
        this.total = total
        this.workLogs = logsData.map((log) => ({
          ...log,
          employee: log.employeeId || {},
          taskType: log.taskTypeId || {}
        }))
      } catch (err) {
        console.error('Failed to fetch work logs:', err)
      } finally {
        this.loading = false
      }
    },
    formatDateTime (val) {
      if (!val) return '-'
      return dayjs(val).format('DD/MM/YYYY HH:mm:ss')
    },
    formatDuration (ms) {
      if (!ms) return '-'
      const seconds = Math.floor((ms / 1000) % 60)
      const minutes = Math.floor((ms / (1000 * 60)) % 60)
      const hours = Math.floor(ms / (1000 * 60 * 60))
      const parts = []
      if (hours > 0) parts.push(`${hours}h`)
      if (minutes > 0 || hours > 0) parts.push(`${minutes}m`)
      parts.push(`${seconds}s`)
      return parts.join(' ')
    },
    onDateChange () {
      if (this.dates.length === 2) {
        this.dateMenu = false
        this.fetchLogs()
      }
    },
    async exportXLSX () {
      if (this.dates.length !== 2) return
      this.loading = true
      try {
        const sorted = [...this.dates].sort()
        const { data: { results } } = await provider.getWorkLogsByPaginate({
          startDate: sorted[0],
          endDate: sorted[1],
          page: 1,
          limit: 99999,
          sortBy: this.options.sortBy[0],
          sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc'
        })

        const rows = (results || []).map((row) => ({
          'Employee Code': row.employeeId?.employeeCode || '',
          'Employee': `${row.employeeId?.firstName || ''} ${row.employeeId?.lastName || ''}`.trim(),
          'Task Department': row.taskTypeId?.department || '',
          'Task': row.taskTypeId?.name || '',
          'Start Date': this.formatDateTime(row.startAt),
          'End Date': this.formatDateTime(row.endAt),
          'Duration': Math.round(row.duration / 60000),
          'Qty': row.quantity || 0,
          'Status': row.state || ''
        }))

        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Work Logs')
        XLSX.writeFile(wb, `warehouse_logs_${sorted[0]}_${sorted[1]}.xlsx`)
      } catch (err) {
        console.error('Failed to export XLSX:', err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* ปรับแต่งความสวยงามเพิ่มเติม */
.v-data-table >>> thead th {
  background-color: #f8f9fa !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.05em;
}
</style>
